import { DataTable } from "@/components/dashboard/integrations/shopify/data-table";
import { getColumns } from "@/components/dashboard/integrations/shopify/table-helper";
import GatedOverlay from "@/components/ui/gated-overlay";
import { ebayAccountsQueryOpts } from "@/hooks/ebay";
import { isGated } from "@/hooks/gated";
import { shopifyProductListQueryOpts } from "@/hooks/shopify-product";
import { getOrgIdWithFallback } from "@/lib/utils";
import { useAuth } from "@/providers/auth";
import { useOrg } from "@/providers/organization";
import {
  useQuery,
  useQueryErrorResetBoundary,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { InventoryFilters, ProductSort } from "@/hooks/product";
import { DEFAULT_PRODUCT_SORT } from "@/hooks/product";

export const Route = createFileRoute(
  "/$org/integrations/shopify/$alias/import",
)({
  component: RouteComponent,
  loader: async ({ context, params }) => {
    const { getToken, setToken, handleTokenInvalid } = context.auth;
    const orgId = getOrgIdWithFallback(context.org.orgId);

    const gated = await isGated(
      {
        token: getToken() as string,
        setToken,
        orgId: orgId,
      },
      handleTokenInvalid,
    );

    context.queryClient.ensureQueryData(
      shopifyProductListQueryOpts(params.alias, {
        setToken,
        token: getToken() as string,
        orgId: orgId,
      }),
    );

    context.queryClient.ensureQueryData(
      ebayAccountsQueryOpts({
        setToken,
        token: getToken() as string,
        orgId: orgId,
      }),
    );

    return { gated: gated?.is_gated };
  },
  errorComponent: ({ error }) => {
    const { t } = useTranslation();
    const router = useRouter();
    const queryErrorResetBoundary = useQueryErrorResetBoundary();

    useEffect(() => {
      queryErrorResetBoundary.reset();
    }, [queryErrorResetBoundary]);

    return (
      <div>
        {error instanceof Error ? error.message : String(error)}
        <button
          onClick={() => {
            router.invalidate();
          }}
        >
          {t("common.retry")}
        </button>
      </div>
    );
  },
});

function RouteComponent() {
  const { gated } = Route.useLoaderData();
  const { org } = Route.useParams();
  const { alias } = Route.useParams();
  const { getToken, setToken } = useAuth();
  const { orgId } = useOrg();
  const resolvedOrgId = getOrgIdWithFallback(orgId);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 30 });
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<InventoryFilters>({
    sync: "all",
    pcIds: [], // no linked store is hidden
    collectionIds: [],
  });
  const [sort, setSort] = useState<ProductSort>(DEFAULT_PRODUCT_SORT);
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    setPagination((currentPagination) =>
      currentPagination.pageIndex === 0
        ? currentPagination
        : { ...currentPagination, pageIndex: 0 },
    );
  }, []);
  const handleFiltersChange = useCallback((value: InventoryFilters) => {
    setFilters(value);
    setPagination((currentPagination) =>
      currentPagination.pageIndex === 0
        ? currentPagination
        : { ...currentPagination, pageIndex: 0 },
    );
  }, []);
  const handleSortChange = useCallback((value: ProductSort) => {
    setSort(value);
    setPagination((currentPagination) =>
      currentPagination.pageIndex === 0
        ? currentPagination
        : { ...currentPagination, pageIndex: 0 },
    );
  }, []);
  const { data: products } = useQuery(
    shopifyProductListQueryOpts(
      alias,
      {
        setToken,
        token: getToken() as string,
        orgId: resolvedOrgId,
      },
      pagination.pageIndex + 1,
      pagination.pageSize,
      filters,
      searchTerm,
      sort,
    ),
  );
  const { data: ebayAccounts } = useSuspenseQuery(
    ebayAccountsQueryOpts({
      setToken,
      token: getToken() as string,
      orgId: resolvedOrgId,
    }),
  );
  const columns = getColumns(org, (ebayAccounts ?? []).length > 0);

  if (gated) {
    return <GatedOverlay org={org} />;
  }

  return (
    <div>
      <DataTable
        columns={columns}
        data={products?.products ?? []}
        ebayAccounts={ebayAccounts ?? []}
        pagination={pagination}
        pageCount={products?.pagination.totalPages ?? 1}
        totalCount={products?.pagination.totalCount ?? 0}
        onPaginationChange={setPagination}
        searchValue={searchTerm}
        onSearchChange={handleSearchChange}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        sort={sort}
        onSortChange={handleSortChange}
      />
    </div>
  );
}
